import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

type Alert = {
  id: string;
  tenantId: string;
  tenantName?: string;
  alertId: string;
  source?: string;
  type?: string;
  severity?: string;
  status?: string;
  createTime?: string;
  endTime?: string;
  feedback?: string;
  alertData?: string; // Full Google Alert Center JSON stored at sync time
};

const SEVERITY_COLORS: Record<string, string> = {
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
};

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-red-50 text-red-700',
  IN_REVIEW: 'bg-yellow-50 text-yellow-700',
  RESOLVED: 'bg-green-50 text-green-700',
};

// ─── Alert Detail Renderer ────────────────────────────────────────────────────

/** Renders the MailPhishing data block (User reported phishing, Suspicious message, Phishing reclassification). */
function MailPhishingDetail({ data }: { data: any }) {
  return (
    <div className="space-y-3">
      {/* Malicious sender */}
      {data.maliciousEntity && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Malicious Sender
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2.5 space-y-0.5">
            {data.maliciousEntity.displayName && (
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {data.maliciousEntity.displayName}
              </p>
            )}
            {data.maliciousEntity.fromHeader && (
              <p className="text-xs font-mono text-red-700 dark:text-red-400">
                {data.maliciousEntity.fromHeader}
              </p>
            )}
            {data.maliciousEntity.entity?.emailAddress &&
              data.maliciousEntity.entity.emailAddress !== data.maliciousEntity.fromHeader && (
              <p className="text-xs font-mono text-gray-500 dark:text-gray-400">
                Envelope: {data.maliciousEntity.entity.emailAddress}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Affected messages */}
      {data.messages?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Affected Message{data.messages.length > 1 ? `s (${data.messages.length})` : ''}
          </p>
          <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
            {data.messages.map((msg: any, i: number) => (
              <div
                key={i}
                className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-2.5 text-xs space-y-1"
              >
                {msg.recipient && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">To:</span>
                    <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{msg.recipient}</span>
                  </div>
                )}
                {msg.subjectText && (
                  <div className="flex items-start gap-1.5">
                    <span className="text-gray-400 shrink-0">Subject:</span>
                    <span className="text-gray-700 dark:text-gray-300 italic">
                      &ldquo;{msg.subjectText}&rdquo;
                    </span>
                  </div>
                )}
                {(msg.date || msg.sentTime) && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">Sent:</span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {new Date(msg.sentTime || msg.date).toLocaleString()}
                    </span>
                  </div>
                )}
                {msg.messageBodySnippet && (
                  <details className="mt-1">
                    <summary className="text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                      Message body snippet
                    </summary>
                    <p className="mt-1 text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded p-1.5 leading-relaxed text-[11px] italic">
                      {msg.messageBodySnippet.length > 400
                        ? msg.messageBodySnippet.substring(0, 400) + '…'
                        : msg.messageBodySnippet}
                    </p>
                  </details>
                )}
                {msg.attachmentsSha256Hash?.length > 0 && (
                  <p className="text-gray-400">
                    {msg.attachmentsSha256Hash.length} attachment{msg.attachmentsSha256Hash.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {typeof data.isInternal === 'boolean' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Origin:{' '}
          <span className={data.isInternal ? 'text-orange-600' : 'text-gray-700 dark:text-gray-300'}>
            {data.isInternal ? 'Internal email (sent from within your domain)' : 'External sender'}
          </span>
        </p>
      )}
    </div>
  );
}

/** Renders the AccountWarning data block (Suspicious login, Leaked password). */
function AccountWarningDetail({ data }: { data: any }) {
  return (
    <div className="space-y-3">
      {data.email && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Affected Account
          </p>
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-3 py-2">
            <p className="text-sm font-mono text-gray-800 dark:text-gray-200">{data.email}</p>
          </div>
        </div>
      )}

      {data.loginDetails && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Login Details
          </p>
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-2.5 space-y-1.5 text-xs">
            {data.loginDetails.ipAddress && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 w-16 shrink-0">IP Address</span>
                <span className="font-mono text-gray-800 dark:text-gray-200">{data.loginDetails.ipAddress}</span>
              </div>
            )}
            {data.loginDetails.loginTime && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 w-16 shrink-0">Login Time</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {new Date(data.loginDetails.loginTime).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Renders the SensitiveAdminAction data block (password resets, SSO changes). */
function SensitiveAdminActionDetail({ data }: { data: any }) {
  // Determine what sub-event occurred
  const eventKey = Object.keys(data).find((k) => k.endsWith('Event'));
  const subEvent = eventKey ? data[eventKey] : null;

  const eventLabel: Record<string, string> = {
    superAdminPasswordResetEvent: 'Super Admin Password Reset',
    adminPasswordChangedEvent: 'Admin Password Changed',
    ssoProfileCreatedEvent: 'SSO Profile Created',
    ssoProfileUpdatedEvent: 'SSO Profile Updated',
    ssoProfileDeletedEvent: 'SSO Profile Deleted',
  };

  return (
    <div className="space-y-3">
      {/* Who performed the action */}
      {data.actorEmail && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Performed By
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
            <p className="text-sm font-mono text-gray-800 dark:text-gray-200">{data.actorEmail}</p>
            <p className="text-xs text-gray-500 mt-0.5">Administrator</p>
          </div>
        </div>
      )}

      {/* Sub-event details */}
      {eventKey && (
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
            Action
          </p>
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-2.5 text-xs space-y-1.5">
            <p className="font-medium text-gray-800 dark:text-gray-200">
              {eventLabel[eventKey] || eventKey.replace(/([A-Z])/g, ' $1').trim()}
            </p>
            {subEvent?.userEmail && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 shrink-0">Target User</span>
                <span className="font-mono text-gray-700 dark:text-gray-300">{subEvent.userEmail}</span>
              </div>
            )}
            {subEvent?.inboundSsoProfileName && (
              <div className="flex items-start gap-2">
                <span className="text-gray-400 shrink-0">SSO Profile</span>
                <span className="font-mono text-gray-700 dark:text-gray-300 break-all text-[10px]">
                  {subEvent.inboundSsoProfileName}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {data.eventTime && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Event time: {new Date(data.eventTime).toLocaleString()}
        </p>
      )}
    </div>
  );
}

/** Main details panel — parses alertData JSON and delegates to type-specific sub-components. */
function AlertDetailsPanel({ alert }: { alert: Alert }) {
  if (!alert.alertData) {
    return (
      <p className="text-sm text-gray-400 italic py-2">No detail data available for this alert.</p>
    );
  }

  let parsed: any;
  try {
    parsed = JSON.parse(alert.alertData);
  } catch {
    return <p className="text-sm text-gray-400 italic py-2">Could not parse alert data.</p>;
  }

  const data = parsed.data || {};
  const atType: string = data['@type'] || '';
  const invLink = parsed.securityInvestigationToolLink
    ? `https://${parsed.securityInvestigationToolLink}`
    : null;

  const isMailPhishing = atType.includes('MailPhishing') || atType.includes('ReportedPhishing');
  const isAccountWarning = atType.includes('AccountWarning');
  const isSensitiveAdmin = atType.includes('SensitiveAdminAction');

  return (
    <div className="space-y-4">
      {/* Metadata grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div>
          <span className="text-gray-400 block mb-0.5">Alert ID</span>
          <span className="font-mono text-gray-700 dark:text-gray-300 text-[10px] break-all">
            {alert.alertId}
          </span>
        </div>
        <div>
          <span className="text-gray-400 block mb-0.5">Start Time</span>
          <span className="text-gray-700 dark:text-gray-300">
            {parsed.startTime ? new Date(parsed.startTime).toLocaleString() : '—'}
          </span>
        </div>
        <div>
          <span className="text-gray-400 block mb-0.5">End Time</span>
          <span className="text-gray-700 dark:text-gray-300">
            {parsed.endTime ? new Date(parsed.endTime).toLocaleString() : '—'}
          </span>
        </div>
        {data.domainId?.customerPrimaryDomain && (
          <div>
            <span className="text-gray-400 block mb-0.5">Domain</span>
            <span className="text-gray-700 dark:text-gray-300">{data.domainId.customerPrimaryDomain}</span>
          </div>
        )}
        {invLink && (
          <div className="col-span-2 pt-1 border-t border-gray-200 dark:border-gray-700 mt-1">
            <a
              href={invLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-blue-500 hover:text-blue-400 font-medium"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View in Security Investigation Tool
            </a>
          </div>
        )}
      </div>

      {/* Type-specific content */}
      {isMailPhishing && <MailPhishingDetail data={data} />}
      {isAccountWarning && <AccountWarningDetail data={data} />}
      {isSensitiveAdmin && <SensitiveAdminActionDetail data={data} />}
    </div>
  );
}

// ─── Main Dashboard Component ─────────────────────────────────────────────────

interface AlertDashboardProps {
  onViewChange?: (view: string) => void;
}

export default function AlertDashboard({ onViewChange }: AlertDashboardProps) {
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [syncMessage, setSyncMessage] = useState('');

  // Multi-select state for bulk operations
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<string>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Fetch tenants for selector
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const result = await client.models.Tenant.list();
      return result.data;
    },
  });

  // Fetch alerts with polling every 30 seconds
  const { data: alertsData, isLoading, error } = useQuery({
    queryKey: ['alerts', selectedTenantId, severityFilter, statusFilter],
    queryFn: async () => {
      const result = await client.queries.fetchAlerts({
        tenantId: selectedTenantId || undefined,
        severity: severityFilter || undefined,
        status: statusFilter || undefined,
        limit: 1000, // Lambda paginates DynamoDB internally; fetch up to 1000 per load
      });
      const raw = result.data;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed || { items: [], count: 0 };
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const allAlerts: Alert[] = alertsData?.items || [];

  // Build a sorted unique list of alert types from the currently loaded set.
  // This is a client-side filter — type is a freeform Google string so we derive
  // the options dynamically rather than maintaining a hardcoded enum.
  const alertTypes = [...new Set(allAlerts.map((a) => a.type).filter(Boolean))].sort() as string[];

  // Apply the type filter locally (no extra API call needed).
  const alerts: Alert[] = typeFilter
    ? allAlerts.filter((a) => a.type === typeFilter)
    : allAlerts;

  // Clear selection whenever the filtered alert list changes to avoid stale
  // selections (e.g., user narrows filter then expects clean slate).
  useEffect(() => {
    setSelectedAlertIds(new Set());
  }, [selectedTenantId, severityFilter, statusFilter, typeFilter]);

  // ── Selection helpers ──
  const allVisibleSelected =
    alerts.length > 0 && alerts.every((a) => selectedAlertIds.has(a.id));
  const someVisibleSelected =
    !allVisibleSelected && alerts.some((a) => selectedAlertIds.has(a.id));

  // Sync the native indeterminate state on the select-all checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleAll = () => {
    if (allVisibleSelected || someVisibleSelected) {
      setSelectedAlertIds(new Set());
    } else {
      setSelectedAlertIds(new Set(alerts.map((a) => a.id)));
    }
  };

  const toggleAlert = (id: string) => {
    setSelectedAlertIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Trigger inline sync
  const [syncDetails, setSyncDetails] = useState<any>(null);
  const syncMutation = useMutation({
    mutationFn: async () => {
      const result = await client.mutations.triggerAlertSync({
        tenantIds: selectedTenantId ? [selectedTenantId] : [],
      });
      const raw = result.data;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    },
    onSuccess: (data) => {
      setSyncDetails(data);
      const tenantWord = data?.successCount === 1 ? 'tenant' : 'tenants';
      let msg = `Synced ${data?.alertCount ?? 0} new alert(s) across ${data?.successCount ?? data?.targetTenantCount ?? 0} ${tenantWord}`;
      if (data?.failedCount > 0) {
        msg += ` · ${data.failedCount} tenant(s) failed`;
      }
      setSyncMessage(msg);
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setTimeout(() => { setSyncMessage(''); setSyncDetails(null); }, 20000);
    },
  });

  // Update alert status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ alertId, status, feedback }: { alertId: string; status: string; feedback?: string }) => {
      const result = await client.mutations.updateAlertStatus({ alertId, status, feedback });
      const raw = result.data;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setSelectedAlert(null);
    },
  });

  // Resolve-result banner state (shown after bulk resolve completes)
  const [resolveResult, setResolveResult] = useState<{ successCount: number; failedCount: number } | null>(null);

  // Bulk resolve selected alerts — updates each alert to RESOLVED in DynamoDB
  // and archives it in Google Alert Center. Stays on the Alerts screen after
  // completion and switches the status filter to RESOLVED so the user can see
  // the green RESOLVED badges on the just-resolved alerts.
  const bulkResolveMutation = useMutation({
    mutationFn: async (alertIds: string[]) => {
      const result = await client.mutations.bulkResolveAlerts({ alertIds });
      const raw = result.data;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    },
    onSuccess: (data) => {
      setSelectedAlertIds(new Set());
      // Show result banner and switch to RESOLVED filter so the user can SEE
      // the resolved alerts with green RESOLVED badges (not just "disappear"
      // from the OPEN view, which looks like nothing happened).
      setResolveResult({ successCount: data?.successCount ?? 0, failedCount: data?.failedCount ?? 0 });
      setStatusFilter('RESOLVED');
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setTimeout(() => setResolveResult(null), 15000);
    },
  });

  // Compute top offenders (tenants with most HIGH alerts)
  const topOffenders = Object.entries(
    alerts
      .filter((a) => a.severity === 'HIGH')
      .reduce<Record<string, { name: string; count: number }>>((acc, alert) => {
        const key = alert.tenantId;
        if (!acc[key]) acc[key] = { name: alert.tenantName || key, count: 0 };
        acc[key].count++;
        return acc;
      }, {}),
  )
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header + Sync */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Security Alerts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Live view — refreshes every 30 seconds. Data sourced from Google Alert Center.
          </p>
        </div>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="btn-primary"
        >
          {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {syncMessage && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            syncDetails?.failedCount > 0
              ? 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-300'
              : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
          }`}
        >
          <p>{syncMessage}</p>
          {/* Per-tenant failure detail */}
          {syncDetails?.failures?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {syncDetails.failures.map((f: any, i: number) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="font-semibold shrink-0">{f.tenantName || f.tenantId}:</span>
                  <span className="opacity-90">{f.error || 'Unknown error'}</span>
                </li>
              ))}
            </ul>
          )}
          {/* Per-tenant write-error detail (fetched OK but DynamoDB writes failed) */}
          {syncDetails?.results?.some((r: any) => r.writeErrors > 0) && (
            <ul className="mt-2 space-y-1">
              {syncDetails.results
                .filter((r: any) => r.writeErrors > 0)
                .map((r: any, i: number) => (
                  <li key={i} className="text-xs">
                    <span className="font-semibold">{r.tenantName || r.tenantId}:</span>{' '}
                    fetched {r.fetched} alert(s) from Google but {r.writeErrors} failed to write to the database.
                    Check CloudWatch logs for details.
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {/* Bulk-resolve result banner */}
      {resolveResult && (
        <div
          className={`rounded-md border px-4 py-3 text-sm flex items-center justify-between gap-4 ${
            resolveResult.failedCount > 0
              ? 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-300'
              : 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300'
          }`}
        >
          <span>
            {resolveResult.failedCount === 0
              ? `✓ ${resolveResult.successCount} alert${resolveResult.successCount !== 1 ? 's' : ''} marked RESOLVED and archived in Google Alert Center.`
              : `${resolveResult.successCount} alert${resolveResult.successCount !== 1 ? 's' : ''} resolved · ${resolveResult.failedCount} failed (check Jobs for details).`}
            {' '}The status filter has been set to <strong>RESOLVED</strong> so you can verify the changes.
          </span>
          <button
            onClick={() => setResolveResult(null)}
            className="shrink-0 opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Top Offenders */}
      {topOffenders.length > 0 && (
        <div className="card">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Top Offenders — High Severity Alerts
          </h2>
          <div className="flex flex-wrap gap-3">
            {topOffenders.map(([tenantId, { name, count }]) => (
              <button
                key={tenantId}
                onClick={() => setSelectedTenantId(tenantId)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-200 bg-orange-50 text-orange-800 text-sm font-medium hover:bg-orange-100 transition-colors"
              >
                {name}
                <span className="bg-orange-200 text-orange-900 rounded-full px-2 py-0.5 text-xs font-bold">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tenant
            </label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="input-field"
            >
              <option value="">All Tenants</option>
              {(tenantsData || []).map((t: any) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input-field"
            >
              <option value="">All Types</option>
              {alertTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Severity
            </label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="input-field"
            >
              <option value="">All Severities</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="IN_REVIEW">In Review</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </div>
        </div>
      </div>

      {/* Selection action bar — visible only when ≥1 alert is checked */}
      {selectedAlertIds.size > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 px-4 py-3">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
            {selectedAlertIds.size} alert{selectedAlertIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedAlertIds(new Set())}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium"
            >
              Clear
            </button>
            <button
              onClick={() => bulkResolveMutation.mutate(Array.from(selectedAlertIds))}
              disabled={bulkResolveMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
            >
              {bulkResolveMutation.isPending ? (
                <>Resolving {selectedAlertIds.size}…</>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Mark {selectedAlertIds.size} Resolved
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Alert List */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading alerts...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            Failed to load alerts. Ensure Google Alert Center is set up and the sync job has run.
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No alerts found. Run &ldquo;Sync Now&rdquo; to pull alerts from Google Alert Center.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {/* Select-all checkbox */}
                <th className="w-10 px-3 py-3">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    aria-label="Select all alerts"
                  />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Severity</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Tenant</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Source</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Time</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {alerts.map((alert) => {
                const isChecked = selectedAlertIds.has(alert.id);
                return (
                  <tr
                    key={alert.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isChecked ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                  >
                    {/* Row checkbox */}
                    <td className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleAlert(alert.id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        aria-label={`Select alert ${alert.alertId}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${
                          SEVERITY_COLORS[alert.severity || 'LOW']
                        }`}
                      >
                        {alert.severity || 'LOW'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{alert.type || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{alert.tenantName || alert.tenantId}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{alert.source || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[alert.status || 'OPEN']}`}>
                        {alert.status || 'OPEN'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {alert.createTime ? new Date(alert.createTime).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedAlert(alert)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="p-5 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${
                      SEVERITY_COLORS[selectedAlert.severity || 'LOW']
                    }`}
                  >
                    {selectedAlert.severity || 'LOW'}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedAlert.type || 'Security Alert'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedAlert.tenantName} &middot; {selectedAlert.source}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedAlert(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none ml-4 shrink-0"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 p-5 space-y-5">
              {/* Details section */}
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Alert Details
                </h4>
                <AlertDetailsPanel alert={selectedAlert} />
              </div>

              {/* Status Actions */}
              <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Update Status
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      updateStatusMutation.mutate({ alertId: selectedAlert.id, status: 'IN_REVIEW' })
                    }
                    disabled={updateStatusMutation.isPending}
                    className="flex-1 px-3 py-2 rounded-lg border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm font-medium hover:bg-yellow-100 transition-colors disabled:opacity-50"
                  >
                    Mark In Review
                  </button>
                  <button
                    onClick={() =>
                      updateStatusMutation.mutate({
                        alertId: selectedAlert.id,
                        status: 'RESOLVED',
                        feedback: 'Manually resolved',
                      })
                    }
                    disabled={updateStatusMutation.isPending}
                    className="flex-1 px-3 py-2 rounded-lg border border-green-300 bg-green-50 text-green-800 text-sm font-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
